import type { FotomaniaRuntime, PreviewSink, PreviewSnapshotRow, PreviewStateRow } from './types';
import { getRuntimeQueue } from '../../shared/runtime-queue';

export type PreviewController = {
  start(): void;
  noteUserSelection(index: number): void;
  disconnect(): void;
  // Fotomania (feature, canvas soberano A1, Task 9): resolves once this controller's owned
  // offscreen session (if one was ever created) has fully disposed, or immediately if none
  // was. `boot.ts`'s disposal barrier awaits this before constructing a replacement controller
  // for the same element.
  whenDisposed(): Promise<void>;
};

const EDIT_EVENTS = [
  'afterImageUpload',
  'elementAdd',
  'elementModify',
  'elementRemove',
  'viewResize',
] as const;

const STRUCTURE_EVENTS = ['viewAdd', 'viewDuplicate'] as const;

// Fotomania (feature, canvas soberano A1, Task 9): the offscreen session engine (Task 8) is
// published ONLY as a global factory (`build-frontend.mjs`'s rt-mirror block); this bundle never
// imports it directly (cross-bundle boundary, see `offscreen-preview.ts`'s file header). These
// local shapes are a narrow, duck-typed mirror of `OffscreenPreviewSession`/
// `OffscreenRenderRequest`, carrying only what this module calls.
type OffscreenRenderRequest = {
  state: unknown;
  branch: 'live' | 'saved' | 'authored';
  format?: 'png' | 'jpeg';
  multiplier?: number;
  maxDimension?: number;
  cropToPrintArea?: boolean;
  watermarkUrl?: string | null;
};

type OffscreenRenderResult = {
  dataUrl: string;
  serializedElementCount: number;
  renderedElementCount: number;
  fromCache: boolean;
};

type OffscreenPreviewSession = {
  render(req: OffscreenRenderRequest): Promise<OffscreenRenderResult>;
  cancelPending(): void;
  dispose(): void;
  whenDisposed(): Promise<void>;
};

type OffscreenPreviewSessionFactory = (options?: Record<string, unknown>) => OffscreenPreviewSession;

declare global {
  // eslint-disable-next-line no-var
  var FotomaniaOffscreenPreview: OffscreenPreviewSessionFactory | undefined;
}

// Fotomania (feature, canvas soberano A1, Task 9 hard rule 4): `OffscreenCancelledError`'s and
// `OffscreenDisposedError`'s classes are not published globally (only the session factory is);
// classify by `.name` the same way `fotomania-js.ts`'s own abort classification does.
function isLifecycleAbort(error: unknown): boolean {
  const name = (error as { name?: string } | undefined)?.name;
  return name === 'OffscreenCancelledError' || name === 'OffscreenDisposedError' || name === 'AbortError';
}

export function createPreviewController(
  fotomania: FotomaniaRuntime,
  sink: PreviewSink,
  options: { debounceMs?: number; maxDimension?: number } = {},
): PreviewController {
  const maxDimension = options.maxDimension ?? 150;
  const debounceMs = options.debounceMs ?? 500;
  const previews = new Map<number, string>();
  const cleanups: Array<() => void> = [];
  const timers = new Map<number, ReturnType<typeof setTimeout>>();
  let generation = 0;
  // Fotomania (fix, canvas soberano A1, final review wave, SEV-2): PER-INDEX staleness guard.
  // A single module-scoped counter shared across ALL indices made a multi-hop filmstrip drain
  // (edit A, click B, click C while B's selectViewRaw pends) drop A's already-captured rows at
  // apply time, and made a burst scheduling several indices at once (automatic PhotoBox fill)
  // invalidate every scheduled index but the last. Each capture trigger for index i is stale
  // only when a NEWER request exists for index i specifically; `requestSeq` is the shared
  // monotonic source of new generation values, `requestGenerationByIndex` records which
  // generation currently "owns" each index.
  let requestSeq = 0;
  const requestGenerationByIndex = new Map<number, number>();

  /** Bumps the request generation for every given index to a new, shared value and returns it.
   * Called at trigger time (before any debounce/async work), per hard rule 2. */
  const bumpRequestGeneration = (indices: number[]): number => {
    const nextGeneration = ++requestSeq;
    indices.forEach((index) => requestGenerationByIndex.set(index, nextGeneration));
    return nextGeneration;
  };
  let disconnected = false;
  let started = false;
  let productReady = false;
  let activeIndex = fotomania.currentViewIndex ?? 0;
  let pendingSelection: number | null = null;
  let navigationQueued = false;
  let ownedSelection = false;
  const runtimeQueue = getRuntimeQueue(fotomania);

  // Fotomania (feature, canvas soberano A1, Task 9 hard rule 3): the controller owns ONE
  // AbortController; its signal threads into every hydration call. `disconnect()` aborts it so a
  // hung upload-zone resolver can never retain the controller past disposal.
  const abortController = new AbortController();

  let session: OffscreenPreviewSession | null = null;
  let warnedMissingSession = false;
  let resolveControllerDisposed!: () => void;
  const controllerDisposed = new Promise<void>((resolve) => {
    resolveControllerDisposed = resolve;
  });

  const getSession = (): OffscreenPreviewSession | null => {
    if (session) return session;
    if (disconnected) return null;
    const factory = globalThis.FotomaniaOffscreenPreview;
    if (typeof factory !== 'function') {
      if (!warnedMissingSession) {
        warnedMissingSession = true;
        console.warn(
          '[Fotomania filmstrip] globalThis.FotomaniaOffscreenPreview is unavailable; skipping offscreen captures',
        );
      }
      return null;
    }
    session = factory();
    return session;
  };

  const listen = (event: string, handler: (payload?: any) => void): void => {
    fotomania.on(event, handler);
    cleanups.push(() => fotomania.off?.(event, handler));
  };

  /** Renders every hydrated row offscreen (watermark dropped per the plan's DROP decision).
   * Returns `null` (skip, no application) when the offscreen engine is unavailable. */
  const renderRows = async (rows: PreviewStateRow[]): Promise<Map<number, string> | null> => {
    const activeSession = getSession();
    if (!activeSession) return null;
    const rendered = new Map<number, string>();
    for (const row of rows) {
      if (!row) continue;
      try {
        const result = await activeSession.render({
          state: row.state,
          branch: row.branch,
          maxDimension,
          format: 'png',
          watermarkUrl: null,
        });
        rendered.set(row.index, result.dataUrl);
      } catch (error) {
        if (!isLifecycleAbort(error)) {
          console.warn('[Fotomania filmstrip] preview render failed', error);
        }
      }
    }
    return rendered;
  };

  // Fotomania (fix, canvas soberano A1, final review wave, SEV-2): PER INDEX filtering against
  // `requestGenerationByIndex` at apply time -- an index whose request generation was
  // individually superseded (by a later single-index schedule/flush) is skipped even though the
  // rest of this same batch is still current.
  //
  // Fotomania (fix, canvas soberano Gate 2 round 1, SEV-2): the wholesale `indices === undefined`
  // special case (`previews.clear()` + write EVERY rendered row unconditionally) is REMOVED. A
  // late-finishing wholesale recapture (`recaptureAll()`/`productCreate`) used to blow away a
  // FRESHER single-index preview that had already applied while the wholesale render was still in
  // flight -- `previews.clear()` had no per-index generation check at all. `queueCapture()`
  // always computes its own `targetIndices` (every view index when the caller passes none) and
  // passes them through here, so the wholesale path now runs through the exact same per-index
  // filter as every other caller: an index this request no longer owns (a newer request already
  // superseded it) is left untouched, never blown away.
  const applyRendered = (rendered: Map<number, string>, indices: number[], requestedGeneration: number): void => {
    indices.forEach((index) => {
      if (requestGenerationByIndex.get(index) !== requestedGeneration) return;
      const url = rendered.get(index);
      if (url) previews.set(index, url);
    });
    sink.apply(new Map(previews), fotomania.views);
  };

  // Per-index staleness for a single index (used by the navigation drain, which always targets
  // exactly one outgoing index per hop).
  const staleIndex = (index: number, requestedGeneration: number, productGeneration: number): boolean =>
    disconnected || productGeneration !== generation || requestGenerationByIndex.get(index) !== requestedGeneration;

  // Batch early-bail: skip fetch/render work entirely only once EVERY targeted index has been
  // individually superseded by a newer request (otherwise at least one index in the batch is
  // still current and `applyRendered` filters the rest at apply time).
  const batchStale = (indices: number[], requestedGeneration: number, productGeneration: number): boolean =>
    disconnected ||
    productGeneration !== generation ||
    indices.every((index) => requestGenerationByIndex.get(index) !== requestedGeneration);

  // Fotomania (feature, canvas soberano A1, Task 9 hard rule 1): captures triggered OUTSIDE the
  // queue read via the PUBLIC `getViewPreviewStates` (which enqueues its own synchronous
  // snapshot pass internally, at the runtime level).
  const captureViaPublicRead = async (
    requestedGeneration: number,
    productGeneration: number,
    indices?: number[],
  ): Promise<void> => {
    if (
      disconnected ||
      productGeneration !== generation ||
      !productReady ||
      fotomania.views.length <= 1
    ) {
      return;
    }

    const targetIndices = indices ?? fotomania.views.map((_view, index) => index);
    let rows: PreviewStateRow[];
    try {
      rows = await fotomania.getViewPreviewStates(targetIndices, { signal: abortController.signal });
    } catch (error) {
      if (!batchStale(targetIndices, requestedGeneration, productGeneration) && !isLifecycleAbort(error)) {
        console.warn('[Fotomania filmstrip] preview capture failed', error);
      }
      return;
    }
    if (batchStale(targetIndices, requestedGeneration, productGeneration)) return;

    const rendered = await renderRows(rows);
    if (rendered === null || disconnected || productGeneration !== generation) return;
    applyRendered(rendered, targetIndices, requestedGeneration);
  };

  const queueCapture = (indices?: number[]): void => {
    const targetIndices = indices ?? fotomania.views.map((_view, index) => index);
    const requestedGeneration = bumpRequestGeneration(targetIndices);
    const productGeneration = generation;
    void captureViaPublicRead(requestedGeneration, productGeneration, indices);
  };

  const flush = (index: number): void => {
    if (!productReady || index < 0 || index >= fotomania.views.length) return;
    const timer = timers.get(index);
    if (timer) clearTimeout(timer);
    timers.delete(index);
    queueCapture([index]);
  };

  const clearTimer = (index: number): void => {
    const timer = timers.get(index);
    if (timer) clearTimeout(timer);
    timers.delete(index);
  };

  // Fotomania (feature, canvas soberano A1, Task 9 hard rule 1): the drain runs INSIDE
  // `runtimeQueue.enqueue`. Its per-iteration capture takes the SYNCHRONOUS
  // `capturePreviewSnapshotsRaw` snapshot in-closure (no await between the snapshot and the loop
  // continuing), and defers hydration + offscreen rendering until AFTER the enqueued closure
  // settles -- the queue is never held across an await of hydration.
  const queueNavigation = (): void => {
    if (disconnected || navigationQueued) return;
    navigationQueued = true;
    const navigationGeneration = generation;
    type PendingRawCapture = { rows: PreviewSnapshotRow[]; requestedGeneration: number; index: number };

    const drain = async (): Promise<PendingRawCapture[]> => {
      const captures: PendingRawCapture[] = [];
      while (
        !disconnected &&
        navigationGeneration === generation &&
        pendingSelection !== null
      ) {
        const target = pendingSelection;
        pendingSelection = null;
        const outgoing = activeIndex;
        clearTimer(outgoing);

        try {
          // RAW (canvas soberano A2, Task 15): the drain runs inside `runtimeQueue.enqueue`
          // (this function's own caller), so it uses the un-queued entry directly.
          if (target !== fotomania.currentViewIndex && typeof fotomania.selectViewRaw === 'function') {
            ownedSelection = true;
            await fotomania.selectViewRaw(target);
          }
          activeIndex = fotomania.currentViewIndex ?? target;
          // Fotomania (fix, canvas soberano A1, final review wave, SEV-2): PER-INDEX bump keyed
          // on the outgoing index only, so a later hop's bump (a different index) can never
          // invalidate this hop's still-pending apply.
          const requestedGeneration = bumpRequestGeneration([outgoing]);
          const rows = fotomania.capturePreviewSnapshotsRaw([outgoing]);
          captures.push({ rows, requestedGeneration, index: outgoing });
        } catch (error) {
          if (!disconnected && navigationGeneration === generation) {
            console.warn('[Fotomania filmstrip] view selection failed', error);
          }
        } finally {
          ownedSelection = false;
        }
      }
      return captures;
    };

    const applyCaptures = async (captures: PendingRawCapture[]): Promise<void> => {
      for (const { rows, requestedGeneration, index } of captures) {
        if (staleIndex(index, requestedGeneration, navigationGeneration)) continue;
        let hydrated: PreviewStateRow[];
        try {
          hydrated = await fotomania.hydratePreviewStates(rows, { signal: abortController.signal });
        } catch (error) {
          if (!staleIndex(index, requestedGeneration, navigationGeneration) && !isLifecycleAbort(error)) {
            console.warn('[Fotomania filmstrip] preview hydrate failed', error);
          }
          continue;
        }
        if (staleIndex(index, requestedGeneration, navigationGeneration)) continue;
        const rendered = await renderRows(hydrated);
        if (rendered === null || staleIndex(index, requestedGeneration, navigationGeneration)) continue;
        const indices = rows.filter((row): row is NonNullable<typeof row> => Boolean(row)).map((row) => row.index);
        applyRendered(rendered, indices, requestedGeneration);
      }
    };

    const drainPromise = runtimeQueue.enqueue(drain);

    // Fotomania (fix, canvas soberano Gate 2 round 1, SEV-1): clear/re-arm `navigationQueued` as
    // soon as the runtime-queue DRAIN CLOSURE ITSELF settles (this `.finally`, attached directly
    // to `drainPromise`), BEFORE hydration/rendering starts. `applyCaptures` below can genuinely
    // hang (a slow/hung offscreen thumbnail render); before this fix that hang kept
    // `navigationQueued` pinned true forever, so every LATER `noteUserSelection()` early-returned
    // and the filmstrip permanently stopped responding to clicks. Splitting the two means a hung
    // render only stalls its OWN thumbnail; the flag (and thus new clicks) is free the moment the
    // queue itself is drained.
    drainPromise.finally(() => {
      navigationQueued = false;
      if (!disconnected && pendingSelection !== null) queueNavigation();
    });

    void drainPromise.then(applyCaptures).catch((error) => {
      if (!disconnected && navigationGeneration === generation) {
        console.warn('[Fotomania filmstrip] view selection failed', error);
      }
    });
  };

  const schedule = (index: number): void => {
    if (!productReady || index < 0 || index >= fotomania.views.length) return;
    const existing = timers.get(index);
    if (existing) clearTimeout(existing);
    const requestedGeneration = bumpRequestGeneration([index]);
    const productGeneration = generation;
    timers.set(
      index,
      setTimeout(() => {
        timers.delete(index);
        void captureViaPublicRead(requestedGeneration, productGeneration, [index]);
      }, debounceMs),
    );
  };

  const eventIndex = (payload?: any): number => {
    if (Number.isInteger(payload?.index)) return payload.index;
    if (Number.isInteger(payload?.viewIndex)) return payload.viewIndex;
    return activeIndex;
  };

  const recaptureAll = (): void => {
    timers.forEach((timer) => clearTimeout(timer));
    timers.clear();
    previews.clear();
    sink.apply(new Map(), fotomania.views);
    queueCapture();
  };

  return {
    start(): void {
      if (started || disconnected) return;
      started = true;

      listen('productCreate', () => {
        generation += 1;
        productReady = true;
        activeIndex = fotomania.currentViewIndex ?? 0;
        pendingSelection = null;
        previews.clear();
        sink.apply(new Map(), fotomania.views);
        timers.forEach((timer) => clearTimeout(timer));
        timers.clear();
        queueCapture();
      });

      EDIT_EVENTS.forEach((event) => {
        listen(event, (payload) => schedule(eventIndex(payload)));
      });

      listen('viewSelect', (payload) => {
        // Fotomania (fix, canvas soberano A1, Task 9 hard rule 4): `captureDepth` suppression is
        // DELETED. Offscreen captures never switch views anymore, so `ownedSelection` (guarding
        // ONLY the drain's own controller-issued `selectView` calls) is the sole guard needed. A
        // real customer selection arriving during a slow offscreen render must not be discarded.
        if (ownedSelection) return;
        const selected = eventIndex(payload);
        const outgoing = activeIndex;
        activeIndex = selected;
        flush(outgoing);
      });

      STRUCTURE_EVENTS.forEach((event) => {
        listen(event, (payload) => queueCapture([eventIndex(payload)]));
      });

      listen('viewRemove', () => recaptureAll());
      listen('viewReorder', () => recaptureAll());
    },

    noteUserSelection(index: number): void {
      if (
        disconnected ||
        !productReady ||
        !Number.isInteger(index) ||
        index < 0 ||
        index >= fotomania.views.length
      ) {
        return;
      }
      pendingSelection = index;
      queueNavigation();
    },

    disconnect(): void {
      if (disconnected) return;
      disconnected = true;
      generation += 1;
      abortController.abort();
      cleanups.splice(0).forEach((cleanup) => cleanup());
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
      previews.clear();
      pendingSelection = null;
      productReady = false;
      sink.disconnect();
      if (session) {
        const activeSession = session;
        activeSession.whenDisposed().then(resolveControllerDisposed);
        activeSession.dispose();
      } else {
        resolveControllerDisposed();
      }
    },

    whenDisposed(): Promise<void> {
      return controllerDisposed;
    },
  };
}
